import React, { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { adminService, User, UserHistory } from "../../../services/adminService";
import { apiErrorMessage, personName } from "../../tasks/taskUtils";

interface DeleteUserDialogProps {
    user: User | null;
    onClose: () => void;
    onDeleted: (user: User) => void;
    /** Offered when they have history and can't be deleted. */
    onDeactivateInstead: (user: User) => void;
}

/** Permanent delete — only for accounts that never did anything (test or
 * mistaken sign-ups). Anyone with history is offered "Deactivate" instead,
 * so their name stays on what they did. */
export const DeleteUserDialog: React.FC<DeleteUserDialogProps> = ({ user, onClose, onDeleted, onDeactivateInstead }) => {
    const [history, setHistory] = useState<UserHistory | null>(null);
    const [typed, setTyped] = useState("");
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState("");
    const name = user ? personName({ display_name: user.display_name ?? null, email: user.email }) : "";

    useEffect(() => {
        if (!user) return;
        setHistory(null);
        setTyped("");
        setError("");
        let cancelled = false;
        adminService
            .getUserHistory(user.user_id)
            .then((h) => !cancelled && setHistory(h))
            .catch((err) => !cancelled && setError(apiErrorMessage(err, "Couldn't check this account's history.")));
        return () => {
            cancelled = true;
        };
    }, [user]);

    const confirmed = !!user && typed.trim().toLowerCase() === user.email.toLowerCase();

    const remove = async () => {
        if (!user || !confirmed) return;
        setDeleting(true);
        setError("");
        try {
            await adminService.deleteUser(user.user_id);
            onDeleted(user);
            onClose();
        } catch (err) {
            setError(apiErrorMessage(err, "Couldn't delete this account."));
        } finally {
            setDeleting(false);
        }
    };

    const blocked = history && !history.can_delete;

    return (
        <Dialog open={!!user} onClose={deleting ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{blocked ? `${name} can't be deleted` : `Delete ${name} permanently?`}</DialogTitle>
            <DialogContent>
                {!history && !error && (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2" color="text.secondary">
                            Checking what they've done…
                        </Typography>
                    </Stack>
                )}

                {blocked && (
                    <>
                        <Typography variant="body2" sx={{ mb: 1.5 }}>
                            Their name is on records the shop needs to keep. Deleting them would erase who did this:
                        </Typography>
                        <Box component="ul" sx={{ pl: 2.5, my: 0, typography: "body2", "& li": { mb: 0.25 } }}>
                            {history!.counts.slice(0, 8).map((c) => (
                                <li key={c.label}>
                                    <b>{c.count}</b> {c.label}
                                </li>
                            ))}
                            {history!.counts.length > 8 && <li>…and more</li>}
                        </Box>
                        <Alert severity="info" sx={{ mt: 2 }}>
                            Deactivate them instead — they can't sign in, and everything they did keeps their name.
                        </Alert>
                    </>
                )}

                {history?.can_delete && (
                    <>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            {user?.email} has no history in the shop, so the account can be removed completely. This can't be undone.
                        </Typography>
                        <TextField
                            fullWidth
                            autoComplete="off"
                            label="Type their email to confirm"
                            placeholder={user?.email}
                            value={typed}
                            onChange={(e) => setTyped(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && confirmed) {
                                    e.preventDefault();
                                    remove();
                                }
                            }}
                        />
                    </>
                )}

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={deleting}>
                    Cancel
                </Button>
                {blocked && user ? (
                    <Button
                        color="warning"
                        variant="contained"
                        disabled={user.is_active === false}
                        onClick={() => {
                            onClose();
                            onDeactivateInstead(user);
                        }}
                    >
                        {user.is_active === false ? "Already deactivated" : "Deactivate instead"}
                    </Button>
                ) : (
                    <Button color="error" variant="contained" onClick={remove} disabled={!confirmed || deleting}>
                        Delete permanently
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};
